import React from "react";
import { MapPin, AlertTriangle, Info } from "lucide-react";

import { JurisdictionSuggestion } from "hooks/useResolvedJurisdiction";

// Styles are global and loaded centrally via src/styles/App.scss (house convention:
// components do not import their own .scss).

interface JurisdictionAutoNoteProps {
	suggestion: JurisdictionSuggestion | null;
	loading: boolean;
	/** The field currently holds what the lookup found. */
	isAutoSelected: boolean;
	/** Puts the suggestion back after the user picked something else. */
	onApplySuggestion: () => void;
}

/**
 * What the AHJ field is showing and why.
 *
 * Three things it has to make obvious, because filing under the wrong authority is
 * expensive: that the value was filled in from the address rather than chosen, when
 * the answer is shaky enough to be worth checking, and how to get back to the
 * suggestion after overruling it.
 */
const JurisdictionAutoNote: React.FC<JurisdictionAutoNoteProps> = ({
	suggestion,
	loading,
	isAutoSelected,
	onApplySuggestion
}) => {
	if (loading) {
		return (
			<div className="jan-note jan-note-loading">
				<MapPin size={14} />
				<span>Working out which office has authority over this address…</span>
			</div>
		);
	}

	if (!suggestion) return null;

	// Overruled: say what we would have picked, and offer it back.
	if (suggestion.resolved && !isAutoSelected) {
		return (
			<div className="jan-note jan-note-info">
				<Info size={14} />
				<span>
					You picked this yourself. From the address we would have said{" "}
					<strong>{suggestion.jurisdictionName}</strong>.
				</span>
				<button type="button" className="jan-note-action" onClick={onApplySuggestion}>
					Use that instead
				</button>
			</div>
		);
	}

	// Nothing to select — the user fills the field in as before, but at least knows why.
	if (!suggestion.resolved) {
		return (
			<div className="jan-note jan-note-warning">
				<AlertTriangle size={14} />
				<span>{suggestion.message}</span>
			</div>
		);
	}

	const toneClassName = suggestion.needsConfirmation ? "jan-note-warning" : "jan-note-success";

	return (
		<div className={`jan-note ${toneClassName}`}>
			{suggestion.needsConfirmation ? <AlertTriangle size={14} /> : <MapPin size={14} />}
			<span>{suggestion.message}</span>
		</div>
	);
};

export default JurisdictionAutoNote;
